// Process mining: what to improve next, from how work actually flows through courageMFG and how people use it.
// Home is the list of suggestions; each process has its map, paths and cases; "Screens and actions" is the app itself.
import { useMemo, useState } from "react";
import { Link, NavLink, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CommandBar } from "../Shell";
import { ErrorBox, ago } from "../ui";
import { useLoad } from "../hooks";
import { pm, dur, KIND_LABEL, recordHref, type Insight, type Overview, type ProcessView, type PCase } from "./pmApi";
import { ProcessMap, type Detail, type Mode, type Sel } from "./ProcessMap";
import { MiningMap, MiningVariants } from "./MiningSerenity";
import { MiningOntology } from "./MiningOntology";
import "./ser.css";
import "./pm.css";

const Spark = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1l1.6 4.4L14 7l-4.4 1.6L8 13l-1.6-4.4L2 7l4.4-1.6z" fill="currentColor" /></svg>
);
const fmt = (v: number, unit: string) => (unit === "%" ? `${v}%` : unit === "days" ? `${v} ${v === 1 ? "day" : "days"}` : `${v}${unit ? ` ${unit}` : ""}`);

/** The records a finding names, as links. */
function Refs({ i }: { i: Insight }) {
  const ev = i.evidence ?? {};
  const list: string[] = [...(ev.examples ?? []), ...(ev.slowest ?? []).map((x: any) => x.ref), ...(ev.oldest ?? []).map((x: any) => x.ref)].filter((x) => typeof x === "string").slice(0, 6);
  if (!list.length) return null;
  const label = ev.oldest ? "Oldest:" : ev.slowest ? "Slowest:" : "For example:";
  return <div className="pm-refs"><span>{label}</span>{list.map((r) => <Link key={r} to={recordHref(i.process.id, r)}>{r}</Link>)}</div>;
}

export function InsightCard({ i, onChange, compact = false, onShow }: { i: Insight; onChange: (x: Insight) => void; compact?: boolean; onShow?: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [dismissing, setDismissing] = useState(false);
  const [reason, setReason] = useState("");
  const s = i.suggestion;
  const act = async (what: string, f: () => Promise<Insight>) => { setBusy(what); setErr(null); try { onChange(await f()); setDismissing(false); } catch (e) { setErr(e); } finally { setBusy(null); } };
  const edge = i.evidence?.from && i.evidence?.to && i.process.id !== "screens" ? `?edge=${encodeURIComponent(`${i.evidence.from}→${i.evidence.to}`)}` : "";
  return (
    <article className={`pm-card ${i.status !== "new" ? "done" : ""}`}>
      <div className="pm-tags">
        {!compact && <span className="pm-tag">{i.process.name}</span>}
        <span className={`pm-tag ${i.kind}`}>{KIND_LABEL[i.kind] ?? i.kind}</span>
        {s && i.suggestionBy && i.suggestionBy !== "rules" && <span className="pm-tag ai" title={`Written by ${i.suggestionBy}`}><Spark /> AI suggestion</span>}
        {i.status === "accepted" && <span className="pm-tag ok">Taken up</span>}
        {i.status === "dismissed" && <span className="pm-tag">Dismissed</span>}
        {!i.live && i.status !== "dismissed" && <span className="pm-tag ok">No longer seen</span>}
      </div>
      <h3>{i.title}</h3>
      <p className="finding">{i.finding}</p>
      {!compact && <Refs i={i} />}
      {s && (
        <div className="pm-sugg">
          <div className="hd">{s.headline}</div>
          {s.why && !compact && <div className="why">{s.why}</div>}
          {!compact && <ol>{s.steps.map((x, k) => <li key={k}>{x}</li>)}</ol>}
          {s.ai && <div className="ai"><Spark /><span>{s.ai}</span></div>}
        </div>
      )}
      <ErrorBox error={err} />
      {dismissing && (
        <form className="pm-dismiss" onSubmit={(e) => { e.preventDefault(); void act("dismiss", () => pm.dismiss(i.id, reason)); }}>
          <input className="input" autoFocus placeholder="Why not? e.g. already being fixed, or it's how it has to work" value={reason} onChange={(e) => setReason(e.target.value)} />
          <button className="btn" disabled={!reason.trim() || !!busy}>Dismiss</button>
          <button type="button" className="btn" onClick={() => setDismissing(false)}>Cancel</button>
        </form>
      )}
      <div className="pm-foot">
        <span className="metric">{i.metric.label}: <b>{fmt(i.metric.value, i.metric.unit)}</b></span>
        {i.change && i.status === "accepted" && (
          <span className={`pm-change ${i.change.better === true ? "better" : i.change.better === false ? "worse" : ""}`}>
            {i.change.better === null ? `No change yet since taken up` : `${fmt(i.change.from, i.metric.unit)} → ${fmt(i.change.to, i.metric.unit)} since taken up`}
          </span>
        )}
        {i.impactHours ? <span title="Hours a month at stake, estimated from how often it happens">≈ {i.impactHours} h a month</span> : null}
        {s && !compact && <span>Effort {s.effort === "S" ? "small" : s.effort === "M" ? "medium" : "large"}</span>}
        {i.status === "dismissed" && i.reason && <span>“{i.reason}” — {i.decidedBy}</span>}
        <span className="acts">
          {edge && !compact && <Link className="btn" to={`/improve/process-mining/p/${encodeURIComponent(i.process.id)}${edge}`}>Show on the map</Link>}
          {edge && compact && onShow && <button className="btn" onClick={onShow}>Show on the map</button>}
          {i.status === "new" && !dismissing && <button className="btn" onClick={() => setDismissing(true)} disabled={!!busy}>Dismiss</button>}
          {i.status === "new" && <button className="btn primary" onClick={() => act("accept", () => pm.accept(i.id))} disabled={!!busy}>{busy === "accept" ? "Raising…" : "Take it up"}</button>}
          {i.status === "new" && <Link className="btn" to={`/factory/new?insight=${i.id}`} title="Start an Implementation Factory request measured with this number">Make a request</Link>}
          {i.status === "dismissed" && <button className="btn" onClick={() => act("reopen", () => pm.reopen(i.id))} disabled={!!busy}>Reopen</button>}
          {i.issue && <Link className="btn" to={`/r/${i.issue.number}`}>{i.issue.number} · {i.issue.state.replace(/_/g, " ")}</Link>}
        </span>
      </div>
    </article>
  );
}

function MiningNav({ o, current }: { o: Overview | null; current?: string }) {
  return (
    <nav className="pm-nav" aria-label="Process mining">
      <NavLink to="/improve/process-mining" end className={({ isActive }) => (isActive ? "active" : "")}><span>Suggestions</span><span className={`n ${o?.insights.open ? "dot" : ""}`}>{o?.insights.open ?? ""}</span></NavLink>
      <NavLink to="/improve/process-mining/screens" className={({ isActive }) => (isActive ? "active" : "")}><span>Screens and actions</span><span className="n">{o?.insights.screens || ""}</span></NavLink>
      <div className="h">Processes, last 90 days</div>
      {(o?.processes ?? []).map((p) => (
        <Link key={p.id} to={`/improve/process-mining/p/${encodeURIComponent(p.id)}`} aria-current={current === p.id}>
          <span>{p.name}</span><span className={`n ${p.insights ? "dot" : ""}`} title={p.insights ? `${p.insights} suggestion${p.insights === 1 ? "" : "s"}` : undefined}>{p.cases.toLocaleString()}</span>
        </Link>
      ))}
      {o && !o.processes.length && <div className="h">Nothing to map yet. Processes appear as records move through their stages.</div>}
    </nav>
  );
}

// ---------------------------------------------------------------- Home: suggestions

export function ProcessMiningPage() {
  const [sp, setSp] = useSearchParams();
  const view = (sp.get("view") ?? "open") as "open" | "accepted" | "dismissed";
  const [tick, setTick] = useState(0);
  const ov = useLoad(() => pm.overview(), [tick]);
  const list = useLoad(() => pm.insights(view), [view, tick]);
  const [writing, setWriting] = useState<string | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [items, setItems] = useState<Record<string, Insight>>({});
  const o = ov.data;
  const rows = (list.data ?? []).map((i) => items[i.id] ?? i).filter((i) => view !== "open" || i.status === "new");
  const rulesOnly = rows.filter((i) => i.status === "new" && i.suggestionBy === "rules").length;
  const write = async () => {
    setWriting("Writing suggestions…"); setErr(null);
    try { const r = await pm.write(); setWriting(null); setItems({}); setTick((x) => x + 1); if (!r.written) setErr(new Error("The model didn't return usable suggestions. The rules suggestions are still there; try again in a minute.")); }
    catch (e) { setErr(e); setWriting(null); }
  };
  const refresh = async () => { setWriting("Mining…"); try { await pm.refresh(); setItems({}); setTick((x) => x + 1); } catch (e) { setErr(e); } finally { setWriting(null); } };
  return (
    <>
      <CommandBar status={writing ?? (o ? `Mined ${ago(o.lastRun)}` : "Loading…")}>
        {o?.model && <button className="cmd primary" onClick={write} disabled={!!writing || !rulesOnly}><Spark /> Write suggestions with AI</button>}
        <button className="cmd" onClick={refresh} disabled={!!writing}>Mine again now</button>
        <Link className="cmd" to="/improve/process-mining/screens">Screens and actions</Link>
      </CommandBar>
      <div className="page ser">
        <header className="s-head">
          <span className="s-kicker">Process mining</span>
          <h1 className="s-h1">How the work actually runs</h1>
          <p className="s-lede">courageMFG reads each record as it moves from step to step. It shows the path the work takes, where it waits and where it goes back, and suggests what to improve next.</p>
        </header>
        <ErrorBox error={ov.error ?? list.error ?? err} />
        <dl className="kpis boxed">
          <div className="kpi"><dt>Open suggestions</dt><dd>{o?.insights.open ?? "–"}</dd></div>
          <div className="kpi" title="Hours a month the suggestions with a measurable effort would give back, estimated from how often each thing happens"><dt>Hours a month at stake</dt><dd>{o ? o.insights.hours : "–"}</dd><small>where it can be measured</small></div>
          <div className="kpi"><dt>Taken up</dt><dd>{o?.insights.accepted ?? "–"}</dd><small>as improvement issues</small></div>
          <div className="kpi"><dt>Processes mapped</dt><dd>{o?.processes.length ?? "–"}</dd><small>{o ? `${o.processes.reduce((s, p) => s + p.cases, 0).toLocaleString()} records` : ""}</small></div>
          <div className="kpi"><dt>People using courageMFG</dt><dd>{o?.screens.people ?? "–"}</dd><small>last {o?.screens.days ?? 30} days{o && !o.capture ? ", capture off" : ""}</small></div>
        </dl>
        {o && !o.model && (
          <div className="pm-note"><Spark /><span className="grow">These suggestions are written from rules. Add a model key for Courage Chat (ANTHROPIC_API_KEY or GEMINI_API_KEY) and AI will rewrite them for your process.</span></div>
        )}
        <div className="s-grid2 wide-l">
          <section className="win" aria-label="Suggestions">
            <div className="win-bar">
              <div className="win-title">Suggestions <span>what to improve next</span></div>
              <div className="seg" role="group" aria-label="Show">
                {([["open", "To decide"], ["accepted", "Taken up"], ["dismissed", "Dismissed"]] as const).map(([k, n]) => (
                  <button key={k} type="button" aria-pressed={view === k} onClick={() => { setItems({}); setSp(k === "open" ? {} : { view: k }); }}>{n}</button>
                ))}
              </div>
            </div>
            {!list.data ? <div className="empty">Loading…</div> : !rows.length ? (
              <div className="empty">{view === "open" ? "Nothing to decide. courageMFG keeps watching and will suggest the next improvement as patterns show up." : view === "accepted" ? "Nothing taken up yet. Take a suggestion up and it becomes an improvement issue, measured from that day." : "Nothing dismissed."}</div>
            ) : (
              <div className="win-body pm-list">{rows.map((i) => <InsightCard key={i.id} i={i} onChange={(x) => { setItems((m) => ({ ...m, [x.id]: x })); void ov.reload?.(); }} />)}</div>
            )}
          </section>
          <div className="s-stack">
            <MinedProcesses o={o} />
            <MiningOntology />
          </div>
        </div>
      </div>
    </>
  );
}

/** The processes being mined, as buttons to each process page; the biggest first. */
function MinedProcesses({ o }: { o: Overview | null }) {
  const [all, setAll] = useState(false);
  const ps = o?.processes ?? [];
  const shown = all ? ps : ps.slice(0, 12);
  return (
    <section className="win" aria-label="Processes being mined">
      <div className="win-bar">
        <div className="win-title">Processes being mined <span>last 90 days</span></div>
        <span className="tag">{ps.length} {ps.length === 1 ? "process" : "processes"}</span>
      </div>
      <div className="win-body">
        {!o ? <div className="muted">Loading…</div> : !ps.length ? <div className="muted">Nothing to map yet. Processes appear as records move through their stages.</div> : (
          <div className="chips">
            {shown.map((p) => (
              <Link key={p.id} className="chipbtn ms-chip" to={`/improve/process-mining/p/${encodeURIComponent(p.id)}`} title={`${p.cases.toLocaleString()} records${p.insights ? `, ${p.insights} suggestion${p.insights === 1 ? "" : "s"}` : ""}`}>
                {p.name}<span className="num">{p.cases.toLocaleString()}</span>{p.insights > 0 && <i className="ms-dot" aria-label={`${p.insights} suggestion${p.insights === 1 ? "" : "s"}`} />}
              </Link>
            ))}
          </div>
        )}
      </div>
      <div className="win-foot">
        <span><i className="ms-dot" aria-hidden="true" /> has suggestions</span>
        <Link to="/improve/process-mining/screens">Screens and actions{o?.insights.screens ? ` · ${o.insights.screens}` : ""}</Link>
        {ps.length > 12 && <button type="button" className="mini ms-more" onClick={() => setAll((x) => !x)}>{all ? "Show the biggest 12" : `Show all ${ps.length}`}</button>}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- A process

const stepsOf = (p: ProcessView, c: PCase) => (c.v >= 0 ? p.variants[c.v].steps : c.steps ?? []);

function Path({ steps }: { steps: string[] }) {
  const seen = new Set<string>();
  return <div className="pm-path">{steps.map((s, k) => { const rep = seen.has(s); seen.add(s); return <span key={k} style={{ display: "contents" }}>{k > 0 && <i>→</i>}<span className={rep ? "rep" : ""}>{s}</span></span>; })}</div>;
}

export function ProcessPage() {
  const { id = "" } = useParams();
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const [days, setDays] = useState(90);
  const [minor, setMinor] = useState(false);
  const [mode, setMode] = useState<Mode>("count");
  const [detail, setDetail] = useState<Detail>("more");
  const [tab, setTab] = useState<"map" | "variants" | "cases">("map");
  const [caseFilter, setCaseFilter] = useState<"all" | "open" | "done" | "rework">("all");
  const [items, setItems] = useState<Record<string, Insight>>({});
  const ov = useLoad(() => pm.overview(), []);
  const r = useLoad(() => pm.process(id, days, minor), [id, days, minor]);
  const p = r.data;
  const edgeParam = sp.get("edge");
  const sel: Sel = edgeParam ? { type: "edge", from: edgeParam.split("→")[0], to: edgeParam.split("→")[1] } : sp.get("step") ? { type: "node", id: sp.get("step")! } : null;
  const setSel = (s: Sel) => setSp(s?.type === "edge" ? { edge: `${s.from}→${s.to}` } : s?.type === "node" ? { step: s.id } : {}, { replace: true });

  const flags = useMemo(() => {
    const m = new Map<string, "hot" | "loop" | "auto">();
    for (const i of p?.insights ?? []) {
      if (!i.evidence?.from || !i.evidence?.to) continue;
      const k = `${i.evidence.from}→${i.evidence.to}`;
      if (i.kind === "bottleneck" || i.kind === "handoff") m.set(k, "hot"); else if (i.kind === "rework") m.set(k, "loop"); else if (i.kind === "automate") m.set(k, "auto");
    }
    return m;
  }, [p]);

  const selCases = useMemo(() => {
    if (!p || !sel) return [];
    return p.cases.filter((c) => { const s = stepsOf(p, c); return sel.type === "node" ? s.includes(sel.id) : s.some((x, k) => k > 0 && s[k - 1] === sel.from && x === sel.to); });
  }, [p, sel?.type, sel && (sel.type === "edge" ? sel.from + sel.to : sel.id)]);
  const selEdge = p && sel?.type === "edge" ? p.edges.find((e) => e.from === sel.from && e.to === sel.to) : null;
  const selNode = p && sel?.type === "node" ? p.nodes.find((n) => n.id === sel.id) : null;

  const k = p?.kpis;
  const cases = (p?.cases ?? []).filter((c) => caseFilter === "all" || (caseFilter === "open" ? !c.done : caseFilter === "done" ? c.done : c.rework));
  const maxV = Math.max(1, ...(p?.variants ?? []).map((v) => v.count));
  return (
    <>
      <CommandBar status={p ? `${k!.cases.toLocaleString()} ${p.process.plural}, ${k!.events.toLocaleString()} events` : "Loading…"}>
        <Link className="cmd" to="/improve/process-mining">← Suggestions</Link>
        <select className="pm-select" aria-label="Process" value={id} onChange={(e) => nav(`/improve/process-mining/p/${encodeURIComponent(e.target.value)}`)}>
          {(ov.data?.processes ?? [{ id, name: p?.process.name ?? id }]).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select>
        <select className="pm-select" aria-label="Period" value={days} onChange={(e) => setDays(Number(e.target.value))}>
          {[30, 90, 180, 365].map((d) => <option key={d} value={d}>Last {d} days</option>)}
        </select>
        <label className="pm-check"><input type="checkbox" checked={minor} onChange={(e) => setMinor(e.target.checked)} /> Include notes, calls and tasks</label>
      </CommandBar>
      <div className="page ser">
        <header className="s-head">
          <span className="s-kicker">Process mining</span>
          <h1 className="s-h1">{p?.process.name ?? "Process"}</h1>
          <p className="s-lede">Every {p?.process.noun ?? "record"} from start to finish, as it actually happened{p ? ` in the last ${p.days} days` : ""}.</p>
        </header>
        <ErrorBox error={r.error} />
        {p ? (
          <div className="s-grid2 wide-l">
            <MiningMap key={`m:${p.process.id}`} p={p} />
            <MiningVariants p={p} onAll={() => { setTab("variants"); document.getElementById("pm-full")?.scrollIntoView({ behavior: "smooth", block: "start" }); }} />
          </div>
        ) : !r.error && <div className="win"><div className="empty">Mining…</div></div>}
        <h2 className="s-h2" id="pm-full">Every step and path</h2>
        {k && (
          <dl className="kpis boxed">
            <div className="kpi"><dt>{p!.process.plural[0].toUpperCase() + p!.process.plural.slice(1)}</dt><dd>{k.cases.toLocaleString()}</dd><small>{k.perMonth} a month</small></div>
            <div className="kpi"><dt>Open</dt><dd>{k.open}</dd></div>
            <div className="kpi" title="Start to finish, for finished ones"><dt>Typical time to finish</dt><dd>{dur(k.medianMs)}</dd><small>slowest fifth over {dur(k.p80Ms)}</small></div>
            <div className="kpi" title="Finished without repeating a step"><dt>Straight through</dt><dd>{k.straightPct === null ? "–" : `${k.straightPct}%`}</dd></div>
            <div className="kpi" title="Repeated a step at least once"><dt>Rework</dt><dd>{k.reworkPct === null ? "–" : `${k.reworkPct}%`}</dd></div>
            <div className="kpi" title="Times the next step was done by someone else"><dt>Handoffs each</dt><dd>{k.handoffsPerCase ?? "–"}</dd></div>
            <div className="kpi"><dt>Different paths</dt><dd>{k.variants}</dd></div>
          </dl>
        )}
        <div className="pm-cols">
          <section className="win">
            <div className="pm-bar">
              <div className="seg" role="group" aria-label="View">
                {([["map", "Map"], ["variants", "Paths"], ["cases", "Records"]] as const).map(([t, n]) => <button key={t} type="button" aria-pressed={tab === t} onClick={() => setTab(t)}>{n}</button>)}
              </div>
              <span className="spacer" style={{ flex: 1 }} />
              {tab === "map" && <>
                <div className="seg" role="group" aria-label="Show on arrows"><button type="button" aria-pressed={mode === "count"} onClick={() => setMode("count")}>How often</button><button type="button" aria-pressed={mode === "time"} onClick={() => setMode("time")}>How long</button></div>
                <div className="seg" role="group" aria-label="Detail"><button type="button" aria-pressed={detail === "main"} onClick={() => setDetail("main")}>Main paths</button><button type="button" aria-pressed={detail === "more"} onClick={() => setDetail("more")}>More</button><button type="button" aria-pressed={detail === "all"} onClick={() => setDetail("all")}>All</button></div>
              </>}
              {tab === "cases" && <div className="seg" role="group" aria-label="Filter">{([["all", "All"], ["open", "Open"], ["done", "Finished"], ["rework", "With rework"]] as const).map(([f, n]) => <button key={f} type="button" aria-pressed={caseFilter === f} onClick={() => setCaseFilter(f)}>{n}</button>)}</div>}
            </div>
            {!p ? <div className="empty">{r.error ? "" : "Mining…"}</div> : tab === "map" ? (
              <>
                <div className="pm-map"><ProcessMap nodes={p.nodes} edges={p.edges} starts={p.starts} ends={p.ends} cases={k!.cases} mode={mode} detail={detail} flags={flags} sel={sel} onSelect={setSel} /></div>
                <div className="pm-legend">
                  <span><i />{mode === "count" ? "Thicker: more often; number: times" : "Thicker: more of the total time; label: typical wait"}</span>
                  <span><i className="hot" />Where the time goes</span><span><i className="loop" />Going back</span><span><i className="auto" />courageMFG could do this step</span>
                  <span>↺ step repeated</span>
                </div>
                {(selEdge || selNode) && (
                  <div className="pm-sel">
                    {selEdge && <>
                      <h3>{selEdge.from} → {selEdge.to}{selEdge.back && <span className="pm-tag rework">Goes back</span>}</h3>
                      <div className="pm-stats">
                        <span><b>{selEdge.count}</b> times in <b>{selEdge.cases}</b> {selEdge.cases === 1 ? p.process.noun : p.process.plural}</span>
                        <span>typical <b>{dur(selEdge.medianMs)}</b></span><span>slowest fifth over <b>{dur(selEdge.p80Ms)}</b></span>
                        <span>handed to someone else <b>{Math.round((selEdge.handoffs / selEdge.count) * 100)}%</b>{selEdge.handoffMedianMs !== null && selEdge.sameMedianMs !== null ? ` (${dur(selEdge.handoffMedianMs)} vs ${dur(selEdge.sameMedianMs)} same person)` : ""}</span>
                      </div>
                    </>}
                    {selNode && <>
                      <h3>{selNode.id}</h3>
                      <div className="pm-stats"><span><b>{selNode.cases}</b> {selNode.cases === 1 ? p.process.noun : p.process.plural}</span><span><b>{selNode.events}</b> times</span>{selNode.repeats > 0 && <span>repeated <b>{selNode.repeats}</b> times</span>}</div>
                    </>}
                    <CaseTable p={p} cases={selCases.slice(0, 50)} />
                    {selCases.length > 50 && <div className="muted" style={{ fontSize: 12 }}>The 50 most recent of {selCases.length}.</div>}
                  </div>
                )}
              </>
            ) : tab === "variants" ? (
              <div className="table-wrap"><table className="dgrid">
                <thead><tr><th>Path</th><th style={{ width: 160 }}>{p.process.plural[0].toUpperCase() + p.process.plural.slice(1)}</th><th style={{ width: 110 }}>Typical time</th></tr></thead>
                <tbody>{p.variants.slice(0, 40).map((v) => (
                  <tr key={v.key}><td><Path steps={v.steps} /></td><td><div className="pm-bar-cell"><span className="b" style={{ width: Math.round((v.count / maxV) * 80) }} />{v.count} <span className="muted">({v.pct}%)</span></div></td><td>{v.done ? dur(v.medianMs) : <span className="muted">open</span>}</td></tr>
                ))}</tbody>
              </table></div>
            ) : (
              <CaseTable p={p} cases={cases.slice(0, 300)} />
            )}
          </section>
          <aside className="pm-side win" aria-label="Suggestions">
            <div className="win-bar"><div className="win-title">Suggestions <span>{p ? `${p.insights.length} for ${p.process.plural}` : ""}</span></div></div>
            {p && !p.insights.length && <div className="empty">Nothing stands out in {p.process.plural} right now.</div>}
            {!!p?.insights.length && <div className="win-body pm-list">{p.insights.map((i) => { const x = items[i.id] ?? i; return (
              <InsightCard key={i.id} i={x} compact onChange={(y) => setItems((m) => ({ ...m, [y.id]: y }))}
                onShow={() => { setTab("map"); setSel({ type: "edge", from: x.evidence.from, to: x.evidence.to }); }} />); })}</div>}
          </aside>
        </div>
      </div>
    </>
  );
}

function CaseTable({ p, cases }: { p: ProcessView; cases: PCase[] }) {
  if (!cases.length) return <div className="empty">None.</div>;
  return (
    <div className="table-wrap"><table className="dgrid">
      <thead><tr><th>Record</th><th>Started</th><th>Status</th><th>Time</th><th>Path</th></tr></thead>
      <tbody>{cases.map((c) => (
        <tr key={c.id}>
          <td style={{ whiteSpace: "nowrap" }}>{c.ref ? <Link className="num" to={recordHref(p.process.id, c.ref)}>{c.ref}</Link> : <span className="muted">–</span>}</td>
          <td style={{ whiteSpace: "nowrap" }}>{new Date(c.start).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</td>
          <td style={{ whiteSpace: "nowrap" }}>{c.done ? c.last : <>At {c.last}{c.idleMs > 3 * 864e5 && <span className="muted"> · idle {dur(c.idleMs)}</span>}</>}</td>
          <td style={{ whiteSpace: "nowrap" }}>{dur(c.durationMs)}</td>
          <td><Path steps={stepsOf(p, c)} /></td>
        </tr>))}
      </tbody>
    </table></div>
  );
}

// ---------------------------------------------------------------- Screens and actions

export function ScreensPage() {
  const [tick, setTick] = useState(0);
  const ov = useLoad(() => pm.overview(), [tick]);
  const r = useLoad(() => pm.screens(30), [tick]);
  const [items, setItems] = useState<Record<string, Insight>>({});
  const [err, setErr] = useState<unknown>(null);
  const s = r.data;
  const toggle = async () => { try { await pm.setCapture(!s?.capture); setTick((x) => x + 1); } catch (e) { setErr(e); } };
  return (
    <>
      <CommandBar status={s ? `${s.events.toLocaleString()} screen views and actions, last ${s.days} days` : "Loading…"}>
        <Link className="cmd" to="/improve/process-mining">← Suggestions</Link>
      </CommandBar>
      <div className="page">
        <div className="page-title"><h1>Screens and actions</h1><span className="sub">Where courageMFG itself gets in the way of the work</span></div>
        <ErrorBox error={r.error ?? err} />
        <div className="pm-note">
          <span className="grow">courageMFG records which screens are open and for how long, the changes people make and the Business Central pages they open from it. It counts people; it never shows what one person did. Nothing typed on a screen is recorded.</span>
          {s && <button className="btn" onClick={toggle}>{s.capture ? "Turn capture off" : "Turn capture on"}</button>}
        </div>
        <div className="pm-desk">
          <MiningNav o={ov.data} />
          <section style={{ display: "grid", gap: 12 }}>
            {s && s.insights.length > 0 && <div className="pm-list">{s.insights.map((i) => <InsightCard key={i.id} i={items[i.id] ?? i} onChange={(x) => setItems((m) => ({ ...m, [x.id]: x }))} />)}</div>}
            <section className="card">
              <div className="pm-bar"><b>Screens</b><span className="muted">{s ? `${s.people} ${s.people === 1 ? "person" : "people"}` : ""}</span></div>
              {!s ? <div className="empty">Loading…</div> : !s.screens.length ? <div className="empty">No screen use recorded yet{s.capture ? "" : ": capture is off"}.</div> : (
                <div className="table-wrap"><table className="dgrid">
                  <thead><tr><th>Screen</th><th>Visits</th><th>People</th><th>Time spent</th><th>Typical visit</th><th>Usually next</th><th>Opens in BC</th></tr></thead>
                  <tbody>{s.screens.map((x) => (
                    <tr key={x.path}><td title={x.path}>{x.name}</td><td>{x.visits.toLocaleString()}</td><td>{x.people}</td><td>{x.minutes >= 120 ? `${Math.round(x.minutes / 6) / 10} h` : `${x.minutes} min`}</td>
                      <td>{x.medianSec >= 90 ? `${Math.round(x.medianSec / 6) / 10} min` : `${x.medianSec} s`}</td>
                      <td className="muted">{x.next.map((n) => n.name).join(", ") || "–"}</td><td className="muted">{x.bc.map((b) => `${b.name} (${b.count})`).join(", ") || "–"}</td></tr>))}
                  </tbody>
                </table></div>
              )}
            </section>
            <section className="card">
              <div className="pm-bar"><b>Actions</b><span className="muted">changes made through courageMFG, Courage Chat and MCP</span></div>
              {!s ? <div className="empty">Loading…</div> : !s.actions.length ? <div className="empty">No actions recorded yet.</div> : (
                <div className="table-wrap"><table className="dgrid">
                  <thead><tr><th>Action</th><th>Times</th><th>Rejected</th><th>People</th><th>Typical response</th></tr></thead>
                  <tbody>{s.actions.map((a) => (
                    <tr key={a.path}><td title={a.path}>{a.name}</td><td>{a.count.toLocaleString()}</td><td className={a.fails / a.count >= 0.15 && a.fails >= 5 ? "pm-bad" : ""}>{a.fails ? `${a.fails} (${Math.round((a.fails / a.count) * 100)}%)` : "–"}</td><td>{a.people}</td><td>{a.medianMs >= 1000 ? `${Math.round(a.medianMs / 100) / 10} s` : `${a.medianMs} ms`}</td></tr>))}
                  </tbody>
                </table></div>
              )}
            </section>
          </section>
        </div>
      </div>
    </>
  );
}
